"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  index: "F.01",
  title: "Rule-First Resolution",
  description: "A deterministic layer checks every step against the mark scheme's expected forms and a library of known error patterns before anything else runs, resolving clear cases for free, with full interpretability.",
  linkLabel: "Explore the rule layer",
  to: "/RuleLayerPage",
  image: {
    label: "RULE LAYER"
  },
  lines: ['// classifier/extraction.py: rule tier', '› MATCH student_step AGAINST MarkScheme criteria', '› LABEL correct | process_error | knowledge_gap', '// source: "rule", zero-cost, deterministic'],
  imageRight: true
}, {
  index: "F.02",
  title: "Model Escalation",
  description: "Only steps the rule layer can't resolve confidently are escalated to a prompted language-model classifier, which uses the student's complete working to judge whether a step is a procedural slip or a genuine gap in method.",
  linkLabel: "View escalation logic",
  to: "/ModelEscalationPage",
  image: {
    label: "ESCALATION"
  },
  lines: ['// classifier/extraction.py: LLM fallback tier', '› ESCALATE steps unresolved by the rule layer', '› classify() falls back to classifier.baseline', '// source: "llm", full student working evaluated'],
  imageRight: false
}, {
  index: "F.03",
  title: "Fine-Tuning Roadmap",
  description: "Once enough labelled submissions exist, a smaller open model is fine-tuned on real classified data, moving the production path toward a faster, self-contained classifier.",
  linkLabel: "See the fine-tuning path",
  to: "/FineTuningRoadmapPage",
  image: {
    label: "FINE-TUNING"
  },
  lines: ['// classifier/finetune/train_lora.py', '› FORMAT gold examples -> SFT training pairs', '› TRAIN open-weights model (e.g. Qwen2.5-7B) via LoRA', '› GATE: ships only once it beats engine=hybrid on held-out data'],
  imageRight: true
}];
function CornerTicks() {
  return <>
      <span className="pointer-events-none absolute -top-px -left-px h-2.5 w-2.5 border-t border-l border-[var(--primary)]" aria-hidden="true" />
      <span className="pointer-events-none absolute -top-px -right-px h-2.5 w-2.5 border-t border-r border-[var(--primary)]" aria-hidden="true" />
      <span className="pointer-events-none absolute -bottom-px -left-px h-2.5 w-2.5 border-b border-l border-[var(--primary)]" aria-hidden="true" />
      <span className="pointer-events-none absolute -bottom-px -right-px h-2.5 w-2.5 border-b border-r border-[var(--primary)]" aria-hidden="true" />
    </>;
}
function VioletBeam({
  className = ""
}: {
  className?: string;
}) {
  return <div className={`flex items-center gap-2 ${className}`} aria-hidden="true">
      <div className="h-px flex-1 bg-border" />
      <div className="h-1.5 w-1.5 rounded-md bg-[var(--primary)]" />
    </div>;
}
export default function Architecture() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1888" id="system-architecture" className="relative bg-background text-foreground py-28 md:py-36 overflow-hidden">
      <div ref={header.ref} className={`relative mx-auto max-w-[880px] px-5 md:px-8 text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <span aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-[calc(50%+60px)] font-default font-semibold text-[6rem] sm:text-[9rem] md:text-[16rem] lg:text-[22rem] leading-none tracking-tighter select-none" style={{
        WebkitTextStroke: "1px var(--border)",
        color: "transparent"
      }}>
          02
        </span>

        <div className="relative z-10">
          <p className="font-mono text-xs md:text-sm uppercase tracking-[0.12em] text-muted-foreground mb-6">
            System Architecture
          </p>
          <h2 className="font-default font-semibold text-[clamp(2.25rem,4.4vw,4rem)] leading-[1.02] tracking-tight">
            Engineered for{" "}
            <span className="italic" style={{
            WebkitTextStroke: "1px var(--primary)",
            color: "transparent"
          }}>
              learning
            </span>
            .
          </h2>
          <VioletBeam className="mt-8 mx-auto max-w-xs" />
        </div>
      </div>

      <div className="relative mx-auto max-w-[1320px] px-5 md:px-8 mt-20 md:mt-28">
        {features.map((feature, i) => {
        const row = useRevealOnScroll<HTMLDivElement>({
          threshold: 0.15
        });
        const imageOnRight = feature.imageRight;
        return <div key={i} data-index={i}>
              <div ref={row.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center transition-all duration-700 ease-out ${row.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                <div className={`lg:col-span-7 ${imageOnRight ? "lg:order-1" : "lg:order-2"}`}>
                  <p className="font-mono text-xs uppercase tracking-[0.12em] text-[var(--primary)] mb-4">
                    {feature.index}
                  </p>
                  <h3 className="font-default font-medium text-[clamp(1.5rem,2vw,2rem)] leading-[1.15] tracking-tight mb-5">
                    {feature.title}
                  </h3>
                  <p className="font-default text-[clamp(1.0625rem,1.25vw,1.25rem)] leading-[1.6] text-muted-foreground max-w-xl mb-8">
                    {feature.description}
                  </p>
                  <Link to={feature.to} className="group inline-flex items-center gap-2 text-foreground hover:text-[var(--primary)] transition-colors duration-200">
                    <span className="font-default font-medium text-sm md:text-base">
                      {feature.linkLabel}
                    </span>
                    <ArrowRight className="size-4 text-[var(--primary)] transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </div>

                <div className={`lg:col-span-5 relative ${imageOnRight ? "lg:order-2" : "lg:order-1"}`}>
                  <div aria-hidden="true" className={`pointer-events-none absolute -z-10 h-[220px] w-[220px] sm:h-[300px] sm:w-[300px] md:h-[420px] md:w-[420px] rounded-lg blur-3xl opacity-50 md:opacity-100 ${imageOnRight ? "-bottom-20 -left-20" : "-bottom-20 -right-20"}`} style={{
                background: "radial-gradient(closest-side, hsl(254 70% 15% / 0.35), transparent 70%)"
              }} />

                  <div className="group relative">
                    <div className="relative border border-border bg-card overflow-hidden rounded-md transition-colors duration-300 group-hover:border-[var(--primary)]">
                      <CornerTicks />

                      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary">
                        <div className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-md bg-muted-foreground" />
                          <span className="h-2 w-2 rounded-md bg-muted-foreground" />
                          <span className="h-2 w-2 rounded-md bg-muted-foreground" />
                        </div>
                        <p className="font-mono text-[10px] tracking-[0.12em] text-muted-foreground uppercase">
                          {feature.image.label}
                        </p>
                        <span className="font-mono text-[10px] tracking-[0.12em] text-[var(--primary)] uppercase">
                          LIVE
                        </span>
                      </div>

                      <div className="p-6 md:p-8 bg-card min-h-[180px] sm:min-h-[220px] md:min-h-[260px] flex flex-col justify-center space-y-2.5 transition-transform duration-500 ease-out group-hover:scale-[1.02]">
                        {feature.lines.map((line, lineIndex) => {
                      const isComment = line.startsWith("//");
                      return <div key={lineIndex} className={`font-mono text-xs md:text-sm tracking-tight ${isComment ? "text-muted-foreground/75 italic" : "text-foreground font-medium pl-3"}`}>
                              {line}
                            </div>;
                    })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {i < features.length - 1 && <VioletBeam className="my-16 md:my-24" />}
            </div>;
      })}

        <p className="font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground mt-16 md:mt-24 text-center max-w-3xl mx-auto">
          Early internal testing on a small validation set showed encouraging results, presented as illustrative preliminary findings, not a verified benchmark.
        </p>
      </div>
    </section>;
}